import { createEnhance } from '@halo/zustand-enhance';
import { GroupStatusEnum, useAppStore } from '@halo/chat-sdk-react';
import { toaster } from '@halo/ui-chat-react';
import { type ActionsStore } from './types';

export const useActionsStore = createEnhance<ActionsStore>((set, get) => ({
  // Global search
  globalSearchIsOpen: false,
  globalSearchTab: undefined,
  getGlobalSearchTab: () => {
    const globalSearchTab = get().globalSearchTab;
    set({ globalSearchTab: undefined });
    return globalSearchTab;
  },
  setGlobalSearchIsOpen: (isOpen, tab) =>
    set({ globalSearchIsOpen: isOpen, globalSearchTab: tab }),

  // Set my contact
  myContactSettingIsOpen: false,
  setMyContactSettingIsOpen: (isOpen, user = null, onlyEdit = false) =>
    set({
      myContactSettingIsOpen: isOpen,
      myContactSettingUser: user,
      myContactSettingOnlyEdit: onlyEdit,
    }),
  myContactSettingUser: null,
  myContactSettingOnlyEdit: false,
  needOpenContactsAddIsOpen: false,
  setNeedOpenContactsAddIsOpen: (needOpenContactsAddIsOpen) => set({ needOpenContactsAddIsOpen }),

  // Contacts add
  contactsAddIsOpen: false,
  setContactsAddIsOpen: (isOpen) => set({ contactsAddIsOpen: isOpen }),
  contactsAddReset: true,
  setContactsAddReset: (reset) => set({ contactsAddReset: reset }),

  //Group dialog
  groupOpen: false,
  profileGroup: null,
  setGroupOpen: (isOpen) => set({ groupOpen: isOpen }),
  fetchProfileGroup: async (gid, mode, openProfile = true) => {
    const client = useAppStore.getState().client;
    if (!client) {
      throw new Error('Client not found');
    }

    try {
      const [result, share] = await Promise.all([
        client.Api.session.getGroupInfoAwait(gid),
        client.Api.common.getShareUrl({ group_id: gid }),
      ]);

      if (!result || result?.status === GroupStatusEnum.Unknown) return null;

      const { url: share_url = '' } = share || {};

      if (openProfile) {
        set({ groupOpen: true, profileGroup: { ...result, share_url, mode } });
      }
      return { ...result, share_url };
    } catch (e: any) {
      toaster.error({ title: 'Group Not Found' });
      throw e;
    }
  },

  // Group create
  groupCreateIsOpen: false,
  setGroupCreateIsOpen: (isOpen, initialGroupCreateSelectedUsers = []) =>
    set({ groupCreateIsOpen: isOpen, initialGroupCreateSelectedUsers }),
  initialGroupCreateSelectedUsers: [],
  getInitialGroupCreateSelectedUsers: () => {
    const users = [...(get().initialGroupCreateSelectedUsers ?? [])];
    set({ initialGroupCreateSelectedUsers: [] });
    return users;
  },

  // Group invite
  groupInviteIsOpen: false,
  groupInviteData: null,
  setGroupInviteIsOpen: (isOpen, data) =>
    set({ groupInviteIsOpen: isOpen, groupInviteData: data ?? null }),

  //robot profile
  profileRobot: null,
  profileRobotIsOpen: false,
  setProfileRobot: (robot_id) => {
    const client = useAppStore.getState().client;
    if (!client) {
      throw new Error('Client not found');
    }
    const { setProfileRobotIsOpen } = get();
    client.Api.user.queryUserInfoGlobal(robot_id!).then((robot) => {
      if (robot) {
        set({ profileRobot: robot ?? null });
        setProfileRobotIsOpen(true);
      }
    });
  },
  setProfileRobotIsOpen: (isOpen) => {
    set({ profileRobotIsOpen: isOpen });
  },

  // Delete chat
  deleteChatSession: null,
  deleteChatIsOpen: false,
  setDeleteChatIsOpen: (isOpen, session) =>
    set({ deleteChatIsOpen: isOpen, deleteChatSession: session }),
  deleteChatHistory: true,
  setDeleteChatHistory: (deleteChatHistory) => set({ deleteChatHistory }),

  // Delete message
  deleteMessagesIsOpen: false,
  deleteMessages: [],
  deleteMessagesSession: null,
  deleteOtherMessagesAllow: false,
  deleteOtherMessages: false,
  deleteMessagesOnDelete: () => Promise.resolve(false),
  setDeleteMessagesIsOpen: (
    isOpen,
    session,
    messages,
    isAllowDeleteAll = true,
    deleteMessagesOnDelete = () => Promise.resolve(false)
  ) => {
    const deleteOtherMessagesAllow = isAllowDeleteAll;
    set({
      deleteOtherMessagesAllow,
      deleteOtherMessages: deleteOtherMessagesAllow,
      deleteMessagesIsOpen: isOpen,
      deleteMessages: messages ?? [],
      deleteMessagesSession: session,
      deleteMessagesOnDelete,
    });
  },
  setDeleteOtherMessages: (deleteOtherMessages) => set({ deleteOtherMessages }),

  // Group remove members
  groupRemoveMembersIsOpen: false,
  groupRemoveMembersData: null,
  setGroupRemoveMembersIsOpen: (isOpen, data) =>
    set({ groupRemoveMembersIsOpen: isOpen, groupRemoveMembersData: data ?? null }),

  // Qr code
  qrCodeIsOpen: false,
  setQrCodeIsOpen: (isOpen, data) => set({ qrCodeIsOpen: isOpen, qrCodeData: data ?? null }),
  qrCodeData: null,

  //profile
  profileOpen: false,
  profileEffects: {},
  setProfileOpen: (isOpen, clear = true, readonly = false) => {
    const { profileEffects, setProfileUser } = get();
    set({ profileOpen: isOpen, profileReadonly: readonly });
    if (!isOpen && clear) {
      setProfileUser(null);
    }
    if (!isOpen) {
      Object.values(profileEffects).forEach((effect) => effect());
    }
  },
  profileUser: null,
  setProfileUser: (user) => {
    set({ profileUser: user ?? null });
  },
  fetchProfileUser: (user_id, openProfile = true, readonly = false) => {
    const client = useAppStore.getState().client;
    if (!client) {
      throw new Error('Client not found');
    }

    const { profileEffects, setProfileUser, setProfileOpen } = get();
    const result = client.Api.user.getUserProfiles(user_id!, (user) => {
      if (user) {
        setProfileUser(user);
      }
    });

    profileEffects.userProfileChanged = result.removeListener;

    result.promise.then(() => {
      setProfileOpen(openProfile, false, readonly);
    });

    return result;
  },

  //profile Edit
  profileEdit: false,
  setProfileEdit: (isOpen) => set({ profileEdit: isOpen }),

  // group call
  groupCreateCallIsOpen: false,
  groupCreateCallData: null,
  setGroupCreateCallIsOpen: (isOpen, data) =>
    set({ groupCreateCallIsOpen: isOpen, groupCreateCallData: data ?? null }),

  // lucky
  luckyDialogIsOpen: false,
  setLuckyDialogIsOpen: (isOpen) => set({ luckyDialogIsOpen: isOpen }),
  luckyDetailsDialogIsOpen: false,
  luckyDetailsDialogData: null,
  setLuckyDetailsDialogIsOpen: (isOpen, data) =>
    set({ luckyDetailsDialogIsOpen: isOpen, luckyDetailsDialogData: data ?? null }),

  // transfer
  transferToIsOpen: false,
  setTransferToIsOpen: (isOpen) => {
    set({ transferToIsOpen: isOpen });
  },
  transferDialogIsOpen: false,
  setTransferDialogIsOpen: (isOpen) => set({ transferDialogIsOpen: isOpen }),
  transferConfirmDialogIsOpen: false,
  transferConfirmDialogData: null,
  setTransferConfirmDialogIsOpen: (isOpen, data) =>
    set({ transferConfirmDialogIsOpen: isOpen, transferConfirmDialogData: data ?? null }),
  transferDetailDialogIsOpen: false,
  transferDetailsDialogData: null,
  setTransferDetailDialogIsOpen: (isOpen, data) =>
    set({ transferDetailDialogIsOpen: isOpen, transferDetailsDialogData: data ?? null }),

  //group join request
  groupJoinRequestsIsOpen: false,
  setGroupJoinRequestsIsOpen: (isOpen) => set({ groupJoinRequestsIsOpen: isOpen }),
  //group add admin
  groupAddAdminIsOpen: false,
  groupAddAdminData: null,
  setGroupAddAdminIsOpen: (isOpen, data) =>
    set({ groupAddAdminIsOpen: isOpen, groupAddAdminData: data ?? null }),
  //group desc
  groupDescriptionIsOpen: false,
  groupDescriptionData: null,
  setGroupDescriptionIsOpen: (isOpen, data) =>
    set({ groupDescriptionIsOpen: isOpen, groupDescriptionData: data ?? null }),

  //image preview
  imagePreviewRef: null,
  setImagePreviewRef: (ref) => set({ imagePreviewRef: ref }),

  //clear chat history
  clearChatHistoryIsOpen: false,
  setClearChatHistoryIsOpen: (isOpen, data) =>
    set({ clearChatHistoryIsOpen: isOpen, clearChatHistoryData: data ?? null }),
  clearChatHistoryData: null,

  //User Onboarding
  userOnboardingIsOpen: false,
  setUserOnboardingIsOpen: (isOpen) => set({ userOnboardingIsOpen: isOpen }),

  // Common log report modal
  logReportOpen: false,
  onLogReportOpenChange: (logReportOpen: boolean) => set({ logReportOpen }),
  //
  //report
  reportIsOpen: false,
  setReportIsOpen: (isOpen, data) => set({ reportIsOpen: isOpen, reportData: data ?? null }),
  reportData: null,

  //safety tools
  safetyToolsIsOpen: false,
  setSafetyToolsIsOpen: (isOpen) => set({ safetyToolsIsOpen: isOpen }),

  //exit group
  exitGroupIsOpen: false,
  setExitGroupIsOpen: (isOpen, data) =>
    set({ exitGroupIsOpen: isOpen, exitGroupData: data ?? null }),
  exitGroupData: null,

  //forbid add
  forbidAddIsOpen: false,
  setForbidAddIsOpen: (isOpen) => set({ forbidAddIsOpen: isOpen }),
  forbidAddData: null,
}));
